import React, { useState, useEffect, useCallback } from 'react';
import { Dropp } from '@dropp.cc/payment-sdk';
import './App.css';

const toDatetimeLocalValue = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDatetimeLocalValue = (localValue) => {
  if (!localValue) return '';
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
};

const defaultRecurringExpiryIso = () =>
  new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();

const RECURRING_FREQUENCIES = [
  { label: 'None', value: 'NONE' },
  { label: 'Half Hourly', value: 'HALF_HOURLY' },
  { label: 'Hourly', value: 'HOURLY' },
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Yearly', value: 'YEARLY' }
];

function App() {
  const [environment, setEnvironment] = useState('qa');
  const [merchantId, setMerchantId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [packageName, setPackageName] = useState('');

  const [paymentType, setPaymentType] = useState('standard');
  const [amount, setAmount] = useState('1');
  const [currency, setCurrency] = useState('USD');
  const [signingUrl, setSigningUrl] = useState('https://your-server.com/payment-callback');
  const [preAuthTimeSeconds, setPreAuthTimeSeconds] = useState('3600');
  const [recurringExpiry, setRecurringExpiry] = useState(defaultRecurringExpiryIso);
  const [recurringFrequency, setRecurringFrequency] = useState('MONTHLY');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [sdkInitStatus, setSdkInitStatus] = useState({ type: 'idle', message: '' });
  const [sdkInitInProgress, setSdkInitInProgress] = useState(false);
  const canInitializeSdk =
    merchantId.trim() !== '' &&
    apiKey.trim() !== '' &&
    packageName.trim() !== '' &&
    environment.trim() !== '';

  useEffect(() => {
    if (paymentType === 'preauth' && currency !== 'USD') {
      setCurrency('USD');
    }
  }, [paymentType, currency]);

  const handleInitializeSdk = useCallback(async () => {
    if (processing || sdkInitInProgress) return;

    if (!canInitializeSdk) {
      setSdkInitialized(false);
      setSdkInitStatus({
        type: 'error',
        message: 'Please fill Merchant ID, API Key, Package Name, and Environment before initializing SDK.'
      });
      setResult({
        type: 'error',
        title: 'Missing SDK Configuration',
        message: 'Please fill Merchant ID, API Key, Package Name, and Environment before initializing SDK.',
        data: null
      });
      return;
    }

    setSdkInitInProgress(true);
    try {
      await Dropp.init({
        merchantId,
        apiKey,
        packageName,
        environment
      });
      setSdkInitialized(true);
      setSdkInitStatus({
        type: 'success',
        message: 'SDK initialized successfully.'
      });
      setResult({
        type: 'success',
        title: 'SDK Initialized',
        message: 'SDK initialized successfully. You can proceed with payment now.',
        data: null
      });
      console.log('SDK initialized successfully');
    } catch (error) {
      const errorMessage = error?.details?.response?.errors?.length
        ? error.details.response.errors.join('. ')
        : (error?.message || 'SDK initialization failed');
      console.error('SDK initialization failed:', error);
      setSdkInitStatus({ type: 'error', message: errorMessage });
      setResult({
        type: 'error',
        title: 'SDK Initialization Failed',
        message: errorMessage,
        data: null
      });
      setSdkInitialized(false);
    } finally {
      setSdkInitInProgress(false);
    }
  }, [processing, sdkInitInProgress, canInitializeSdk, merchantId, apiKey, packageName, environment]);

  const formatPaymentError = (error) => {
    if (error?.details?.response?.errors?.length) {
      return error.details.response.errors.join('. ');
    }
    if (error?.details?.errors?.length) {
      return error.details.errors.join('. ');
    }
    return error?.message || 'An error occurred';
  };

  const buildPaymentOptions = useCallback(() => {
    const amountValue = parseFloat(amount);
    const options = {
      paymentType,
      amount: amountValue,
      currency: currency.trim().toUpperCase(),
      itemName: getItemName(paymentType),
      description: getDescription(paymentType),
      invoiceId: `INV-${paymentType.toUpperCase()}-${Date.now()}`
    };

    if (paymentType === 'preauth') {
      options.authHoldTimeInSeconds = parseInt(preAuthTimeSeconds, 10);
      options.callbackUrl = signingUrl.trim();
    } else if (paymentType === 'recurring') {
      options.callbackUrl = signingUrl.trim();
      options.recurringEndDate = recurringExpiry.trim();
      options.frequency = recurringFrequency;
      options.recurringInterval = recurringFrequency;
    }

    return options;
  }, [
    paymentType,
    amount,
    currency,
    signingUrl,
    preAuthTimeSeconds,
    recurringExpiry,
    recurringFrequency
  ]);

  // Handle payment — field validation is performed by the SDK
  const handlePayment = useCallback(async () => {
    if (!sdkInitialized || processing) return;

    setProcessing(true);
    setResult(null);

    const paymentFields = buildPaymentOptions();
    const options = {
      ...paymentFields,
      onSuccess: (result) => {
        console.log('Payment successful:', result);
        setResult({
          type: 'success',
          title: '✓ Payment Successful',
          message: result.message || 'Transaction completed successfully!',
          data: result
        });
        setProcessing(false);
      },
      onFailure: (result) => {
        console.error('Payment failed:', result);
        setResult({
          type: 'error',
          title: '✗ Payment Failed',
          message: result.message || 'Payment transaction failed',
          data: result
        });
        setProcessing(false);
      },
      onCancel: (result) => {
        console.log('Payment cancelled:', result);
        setResult({
          type: 'info',
          title: 'Payment Cancelled',
          message: `Payment was cancelled. ${result.reason ? `Reason: ${result.reason}` : ''}`,
          data: result
        });
        setProcessing(false);
      },
      onClose: () => {
        console.log('Modal closed');
        setProcessing(false);
      }
    };

    console.log('Initiating payment:', paymentFields);

    try {
      await Dropp.pay(options);
    } catch (error) {
      console.error('Payment error:', error);
      const isValidationError = error?.code === 'INVALID_CONFIG';
      setResult({
        type: 'error',
        title: isValidationError ? 'Invalid Payment Configuration' : 'Payment Error',
        message: formatPaymentError(error),
        data: error
      });
      setProcessing(false);
    }
  }, [buildPaymentOptions, sdkInitialized, processing]);

  // Helper functions
  const getItemName = (paymentType) => {
    switch (paymentType) {
      case 'preauth':
        return 'Hotel Reservation - Pre-Authorization';
      case 'recurring':
        return 'Monthly Subscription';
      default:
        return 'Test Product Purchase';
    }
  };

  const getDescription = (paymentType) => {
    switch (paymentType) {
      case 'preauth':
        return 'Authorization hold for booking confirmation';
      case 'recurring':
        return 'Recurring monthly payment';
      default:
        return 'Standard payment transaction';
    }
  };

  const getEnvBadgeClass = () => {
    switch (environment) {
      case 'production':
        return 'badge-production';
      case 'qa':
        return 'badge-qa';
      default:
        return 'badge-sandbox';
    }
  };

  return (
    <div className="app">
      <div className="container">
        {/* Header */}
        <div className="header">
          <h1>🚀 Dropp Payment SDK</h1>
          <p>React Test Application</p>
        </div>

        {/* Environment Selector */}
        {/* <div className="env-selector">
          <label htmlFor="environment">
            Environment
            <span className={`badge ${getEnvBadgeClass()}`}>
              {environment.charAt(0).toUpperCase() + environment.slice(1)}
            </span>
          </label>
          <select
            id="environment"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            disabled={processing}
          >
            <option value="sandbox">Sandbox (Testing)</option>
            <option value="qa">QA</option>
            <option value="production">Production</option>
          </select>
          <p className="info-text">Use Sandbox for testing without real transactions</p>
        </div> */}

        {/* Configuration Card */}
        <div className="card">
          <h3>SDK Configuration</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="merchantId">Merchant ID (Hedera Account)</label>
              <input
                type="text"
                id="merchantId"
                value={merchantId}
                onChange={(e) => {
                  setMerchantId(e.target.value);
                  setSdkInitialized(false);
                  setSdkInitStatus({ type: 'idle', message: '' });
                }}
                placeholder="0.0.123456"
                disabled={processing}
              />
            </div>

            <div className="form-group">
              <label htmlFor="packageName">Package Name</label>
              <input
                type="text"
                id="packageName"
                value={packageName}
                onChange={(e) => {
                  setPackageName(e.target.value);
                  setSdkInitialized(false);
                  setSdkInitStatus({ type: 'idle', message: '' });
                }}
                placeholder="com.dropp.payment"
                disabled={processing}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="apiKey">API Key</label>
            <input
              type="text"
              id="apiKey"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setSdkInitialized(false);
                setSdkInitStatus({ type: 'idle', message: '' });
              }}
              placeholder="4b3a6dd3e..................."
              disabled={processing}
            />
          </div>

          <div className="button-row button-row-single">
            <button
              className="button button-secondary button-compact"
              onClick={handleInitializeSdk}
              disabled={processing || sdkInitInProgress || !canInitializeSdk}
            >
              <span className="icon">⚙️</span>
              <span>{sdkInitInProgress ? 'Initializing SDK...' : 'Initialize SDK'}</span>
            </button>
          </div>

          {sdkInitStatus.type !== 'idle' && (
            <div className={`sdk-init-status sdk-init-status-${sdkInitStatus.type}`}>
              {sdkInitStatus.type === 'success' ? 'SDK Initialized: ' : 'Initialization Error: '}
              {sdkInitStatus.message}
            </div>
          )}
        </div>

        {/* Payment Configuration Card */}
        <div className="card">
          <h3>Payment Configuration</h3>

          <fieldset className="payment-type-fieldset" disabled={processing}>
            <legend className="payment-type-legend">Payment Type</legend>
            <div className="radio-row">
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentType"
                  value="standard"
                  checked={paymentType === 'standard'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span>Standard</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentType"
                  value="preauth"
                  checked={paymentType === 'preauth'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span>Preauth</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="paymentType"
                  value="recurring"
                  checked={paymentType === 'recurring'}
                  onChange={(e) => setPaymentType(e.target.value)}
                />
                <span>Recurring</span>
              </label>
            </div>
          </fieldset>

          <div
            className={`form-row${
              paymentType === 'recurring' || paymentType === 'preauth'
                ? ' form-row-payment-fields'
                : ''
            }`}
          >
            <div className="form-group form-group-narrow">
              <label htmlFor="currency">Currency</label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={processing}
              >
                <option value="USD">USD</option>
                {paymentType !== 'preauth' && <option value="HBAR">HBAR</option>}
                {paymentType !== 'preauth' && <option value="USDC">USDC</option>}
              </select>
            </div>

            <div className="form-group form-group-narrow">
              <label htmlFor="amount">Amount</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                min="0.01"
                disabled={processing}
              />
            </div>

            {paymentType === 'preauth' && (
              <div className="form-group form-group-wide">
                <label htmlFor="preAuthTimeSeconds">Pre-auth Time (Seconds)</label>
                <input
                  type="number"
                  id="preAuthTimeSeconds"
                  value={preAuthTimeSeconds}
                  onChange={(e) => setPreAuthTimeSeconds(e.target.value)}
                  min="1"
                  step="1"
                  disabled={processing}
                />
              </div>
            )}

            {paymentType === 'recurring' && (
              <div className="form-group form-group-wide">
                <label htmlFor="recurringExpiry">Expiry</label>
                <input
                  type="datetime-local"
                  id="recurringExpiry"
                  value={toDatetimeLocalValue(recurringExpiry)}
                  onChange={(e) =>
                    setRecurringExpiry(fromDatetimeLocalValue(e.target.value))
                  }
                  disabled={processing}
                />
              </div>
            )}
          </div>

          {paymentType === 'recurring' && (
            <div className="form-row form-row-recurring-extra">
              <div className="form-group form-group-medium">
                <label htmlFor="recurringFrequency">Frequency</label>
                <select
                  id="recurringFrequency"
                  value={recurringFrequency}
                  onChange={(e) => setRecurringFrequency(e.target.value)}
                  disabled={processing}
                >
                  {RECURRING_FREQUENCIES.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {paymentType === 'preauth' && (
            <div className="form-group">
              <label htmlFor="signingUrl">Signing URL</label>
              <input
                type="url"
                id="signingUrl"
                value={signingUrl}
                onChange={(e) => setSigningUrl(e.target.value)}
                placeholder="https://your-server.com/pre-auth-post-callback"
                disabled={processing}
              />
            </div>
          )}

          {paymentType === 'recurring' && (
            <div className="form-group">
              <label htmlFor="signingUrl">Signing URL</label>
              <input
                type="url"
                id="signingUrl"
                value={signingUrl}
                onChange={(e) => setSigningUrl(e.target.value)}
                placeholder="https://your-server.com/rps-callback"
                disabled={processing}
              />
            </div>
          )}

          <div className="button-row button-row-single">
            <button
              className="button button-primary button-compact"
              onClick={handlePayment}
              disabled={processing || !sdkInitialized}
            >
              <span className="icon">💳</span>
              <span>{processing ? 'Processing...' : 'Proceed'}</span>
            </button>
          </div>
        </div>


        {/* Result Card */}
        {result && (
          <div className={`result ${result.type}`}>
            <div className="result-title">{result.title}</div>
            <div className="result-message">{result.message}</div>
            {result.data && (
              <details className="result-details">
                <summary>View Details</summary>
                <pre>{JSON.stringify(result.data, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="footer">
        Dropp Payment SDK v1.0.6 | React Test Application
      </div>
    </div>
  );
}

export default App;
